import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_eeeed7c0-b14c-41e9-a1b8-b122278f0c69/artifacts/auk1nki7_LOGO-EECASA.png";

export const ForgotPasswordPage = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
      // In development, show the reset token (in production, it would be sent via email)
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F8F9FA] font-['IBM_Plex_Sans',sans-serif]">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="bg-white p-4 rounded-xl shadow-md border border-gray-100">
            <img 
              src={LOGO_URL} 
              alt="Especialistas En Casa" 
              className="h-16 object-contain"
            />
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-semibold font-['Work_Sans',sans-serif] text-gray-900">
              {success ? 'Revisa tu correo' : 'Recuperar Contraseña'}
            </CardTitle>
            <CardDescription className="text-gray-500">
              {success 
                ? 'Te hemos enviado las instrucciones para restablecer tu contraseña'
                : 'Ingresa tu correo electrónico y te enviaremos instrucciones'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                
                <p className="text-center text-sm text-gray-600">
                  Si el correo <strong>{email}</strong> está registrado en nuestro sistema, 
                  recibirás un enlace para restablecer tu contraseña.
                </p>

                {/* Development only: Show reset token */}
                {resetToken && (
                  <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertDescription className="text-yellow-800 text-sm">
                      <strong>Modo desarrollo:</strong> El token normalmente se enviaría por correo.
                      <br />
                      <Link 
                        to={`/reset-password?token=${resetToken}`}
                        className="text-[#E63946] hover:underline font-medium"
                      >
                        Haz clic aquí para restablecer tu contraseña
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="pt-4 space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setSuccess(false);
                      setEmail('');
                      setResetToken('');
                    }}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Enviar a otro correo
                  </Button>
                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full text-gray-600">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Volver al inicio de sesión
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertDescription className="text-red-700">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700">
                    Correo Electrónico
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 border-gray-300 focus:border-[#E63946] focus:ring-[#E63946]"
                    data-testid="forgot-password-email"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-[#E63946] hover:bg-[#C92A36] text-white font-medium transition-colors"
                  data-testid="forgot-password-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar Instrucciones'
                  )}
                </Button>

                <Link to="/login" className="block">
                  <Button variant="ghost" className="w-full text-gray-600">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Volver al inicio de sesión
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          © 2024 Especialistas En Casa. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
